import React from "react";
import {
  usePrimaryOrganization,
  usePrimaryOrganizationId,
  useSelfOrganizationUser,
} from "../../../hooks/useOrganization";
import { RouteHelpers, Routes } from "../../../root/routes";
import { BaseNav } from "./BaseNav";
import {
  DIVIDER_LITERAL,
  NAV_ITEMS,
  NAV_ITEMS_GENERAL_ONLY,
  NAV_ITEMS_HR_ONLY,
  NavDivider,
  NavItem,
} from "./config";
import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";

import { NetworkRequest } from "../../../../common/models";
import { UserRole } from "../../../../common/service/server/Types";
import { useOrganizationVendors, useRequestSummaries } from "../../../hooks";
import { SidebarNavLinkRedesign } from "@truevault/aquilo";
import {
  isHrCenterOnboardingComplete,
  isPrivacyCenterOnboardingComplete,
  isWorkflowsOnboardingComplete,
  TruevaultOnboardingProgress,
  useOnboardingProgress,
} from "../../../../common/hooks/useOnboardingProgress";
import {
  isHrCenterEnabled,
  isHrNoticesEnabled,
  isHrRetentionPolicyEnabled,
} from "../../../pages/stay-compliant/hr-center/HRCenterDashboardPage";
import {
  isDSARFormsEnabled,
  isPrivacyCenterDeveloperInstructionsEnabled,
  isWebsiteConsentEnabled,
} from "../../../pages/stay-compliant/PrivacyCenterDashboard";
import { isVendorDocumentationClassified } from "../../../pages/data-map/vendor-documentation/VendorDocumentationPage";
import { useSurvey } from "../../../../common/hooks/api/useSurvey";
import { DEVELOPER_INSTRUCTIONS_SURVEY_NAME } from "../../../pages/stay-compliant/PrivacyNoticeChecklistItems";
import { DevInstructionsDSARFormsOnboardingSlug } from "../../../pages/stay-compliant/privacy-center/DSARForms";

export const isRequestInboxEnabled = (
  org: OrganizationDto,
  onboardingProgress: TruevaultOnboardingProgress,
) => {
  // Always enable if pending requests
  if (org?.activeRequestCount > 0) {
    return true;
  }

  // Enable if workflows onboarding is complete
  return (
    onboardingProgress &&
    isWorkflowsOnboardingComplete(onboardingProgress.workflowsSetupOnboardingProgress)
  );
};

export const isTaskListEnabled = (
  org: OrganizationDto,
  onboardingProgress: TruevaultOnboardingProgress,
) => {
  // Always enable if pending tasks
  if (org?.activeTaskCount > 0) {
    return true;
  }

  return (
    onboardingProgress &&
    isWorkflowsOnboardingComplete(onboardingProgress.workflowsSetupOnboardingProgress)
  );
};

export const StayCompliantNav: React.FC = () => {
  const [organization, request, refreshOrg] = usePrimaryOrganization();
  const currentOrgUser = useSelfOrganizationUser();
  const primaryOrgId = usePrimaryOrganizationId();
  const [dataRequestPage, ,] = useRequestSummaries(primaryOrgId, "active");

  const isGeneralUser = currentOrgUser?.role === "GENERAL";
  const isHrUser = currentOrgUser?.role === "HR";

  const [onboardingProgress] = useOnboardingProgress(primaryOrgId);

  const [allVendors, ,] = useOrganizationVendors(
    primaryOrgId,
    /* excludeRecipientCategories = */ [],
    /* includeDataStorageLocations = */ false,
    /* excludeIncomplete = */ undefined,
    /* context = */ undefined,
    /* onlyUseSettled = */ true,
  );

  const { answers: devInstructionsAnswers } = useSurvey(
    primaryOrgId,
    DEVELOPER_INSTRUCTIONS_SURVEY_NAME,
  );
  const isDSARFormsOnboardingComplete =
    devInstructionsAnswers &&
    devInstructionsAnswers[DevInstructionsDSARFormsOnboardingSlug] === "true";

  // Don't check for `allVendors` here since we don't want to block nav bar rendering on the vendors API request
  if (!NetworkRequest.areFinished(request) || !onboardingProgress) {
    // If this is ever removed, be sure to add null-checks for the uses of onboardingProgress in this component.
    return null;
  }

  const {
    workflowsSetupOnboardingProgress: {
      isFinishedWithApiIntegrationsSetup,
      isFinishedDataStorageOnboarding,
      isFinishedWorkflowSetupOnboarding,
      isLimitSettingsComplete,
    },
    surveyOnboardingProgress: { isBusinessSurveyFinished, isHrSurveyFinished },
    dataMapOnboardingProgress: { isDataRetentionComplete },
    privacyCenterOnboardingProgress: { isFinishedWithPoliciesNotices },
  } = onboardingProgress;

  const isVendorDocumentationIncomplete =
    allVendors &&
    allVendors.length > 0 &&
    !allVendors.every((v) => isVendorDocumentationClassified(organization, v));

  const isHrCenterSetupRequired = organization?.requiresEmployeeMapping === true;

  // For now, only check the retention finished to determine when the data map onboarding is complete, since we'll let users leave the Vendor Documentation step as un-finished (and the data retention step can only be complete after the preceding steps are done)
  // Since `privacyCenterOnboardingProgress?.isFinishedWithPoliciesNotices` is a "one-way" completion step and can't be undone, if the org has completed that step then we can assume they've completed the data map onboarding
  let areAllDataMapStepsComplete = isDataRetentionComplete || isFinishedWithPoliciesNotices;

  const navItemsConfig = navItemsForRole(currentOrgUser?.role);

  const navItems = (org: OrganizationDto) =>
    navItemsConfig.map((navItem: NavItem | NavDivider, idx: number) => {
      if (navItem === DIVIDER_LITERAL) {
        return <hr key={`divider-${idx}`} className="primary-nav__divider" />;
      }

      const { item, hidden } = navItem as NavItem;
      // Make a deep copy of navItem.children since it may be modified
      let navItemChildrenMutable = JSON.parse(JSON.stringify(navItem.children || [])) as NavItem[];

      const isHidden =
        typeof hidden == "function" ? hidden(org) : typeof hidden == "undefined" ? false : hidden;

      let notificationCount = 0;
      let showIncompleteBadge = false;

      // Don't show request inbox notifications for HR users since they only see HR requests
      if (item == "REQUEST_INBOX" && !isHrUser) {
        notificationCount = dataRequestPage?.meta?.totalCount || org.activeRequestCount;
      }
      if (item == "TASK_LIST") {
        notificationCount = org.activeTaskCount;
      }
      // Hide the HR Center nav item if the org has no employee collection groups
      if (item == "HR_CENTER" && !isHrCenterSetupRequired) {
        return null;
      }

      if (isHidden) {
        return null;
      }

      // Before essential privacy center steps are complete, lock all nav items except: privacy center, knowledge center, and data map
      // Note that since the privacy center onboarding progress may become incomplete with new developer instructions modules, we also unlock the top-level nav items if the API integrations setup is complete (since that setup is one-time)
      let isDisabled = false;
      if (
        !(
          item == "DASHBOARD" ||
          item == "KNOWLEDGE_CENTER" ||
          item == "PRIVACY_CENTER" ||
          item == "DATA_MAP_DASHBOARD"
        )
      ) {
        isDisabled = !(
          isPrivacyCenterOnboardingComplete(
            onboardingProgress.privacyCenterOnboardingProgress,
            /* onlyEssentialSteps = */ true,
          ) || isFinishedWithApiIntegrationsSetup
        );
      }
      // Unlock the "Documents" page after the first step in privacy center onboarding
      if (item == "DOCUMENTS") {
        isDisabled =
          !onboardingProgress.privacyCenterOnboardingProgress.isFinishedWithPoliciesNotices;
      }

      if (item == "TASK_LIST") {
        isDisabled = !isTaskListEnabled(org, onboardingProgress);
      }

      if (item == "REQUEST_INBOX") {
        isDisabled = !isRequestInboxEnabled(org, onboardingProgress);
      }

      // For the Privacy Center top-level nav item, enable/disable the individual privacy center items based on the setup status and current user's role
      if (item == "PRIVACY_CENTER") {
        const privacyCenterOnboardingIncomplete = !isPrivacyCenterOnboardingComplete(
          onboardingProgress.privacyCenterOnboardingProgress,
        );

        const biometricIdentifiersIncomplete =
          !onboardingProgress.privacyCenterOnboardingProgress.isBiometricIdentifiersPolicyFinished;

        isDisabled = !areAllDataMapStepsComplete && !isFinishedWithPoliciesNotices;

        showIncompleteBadge = privacyCenterOnboardingIncomplete || biometricIdentifiersIncomplete;

        for (let child of navItemChildrenMutable) {
          if (child.item == "PRIVACY_CENTER_POLICIES_AND_NOTICES") {
            // This step is always enabled for non-general users since it's the first step
            child.isDisabled = isGeneralUser;
            // Show incomplete badge if biometric identifiers policy is required but incomplete
            child.showIncompleteBadge = biometricIdentifiersIncomplete;
            // Only enable subsequent steps if all the previous steps are completed
          } else if (child.item == "PRIVACY_CENTER_DSAR_FORMS") {
            child.isDisabled = !isDSARFormsEnabled(
              onboardingProgress.privacyCenterOnboardingProgress,
              isDSARFormsOnboardingComplete,
            );
          } else if (child.item == "PRIVACY_CENTER_WEBSITE_CONSENT") {
            child.isDisabled = !isWebsiteConsentEnabled(
              onboardingProgress.privacyCenterOnboardingProgress,
            );
          } else if (child.item == "PRIVACY_CENTER_DEVELOPER_INSTRUCTIONS") {
            child.isDisabled = !isPrivacyCenterDeveloperInstructionsEnabled(
              onboardingProgress.privacyCenterOnboardingProgress,
              isDSARFormsOnboardingComplete,
            );
            child.showIncompleteBadge =
              !child.isDisabled &&
              !onboardingProgress.privacyCenterOnboardingProgress.isDevInstructionsFinished;
          }
        }
      }

      // Similarly for HR Center, enable/disable individual items based on setup status
      if (item === "HR_CENTER") {
        isDisabled = !isHrCenterEnabled(onboardingProgress);

        showIncompleteBadge = !isHrCenterOnboardingComplete(
          onboardingProgress.hrCenterOnboardingProgress,
        );

        for (let child of navItemChildrenMutable) {
          if (child.item == "HR_CENTER_HR_DATA_MAP") {
            // This step is always enabled since it's the first step
            child.isDisabled = false;
          } else if (child.item == "HR_CENTER_EDIT_HR_RETENTION_POLICY") {
            child.isDisabled = !isHrRetentionPolicyEnabled(
              onboardingProgress.hrCenterOnboardingProgress,
            );
          } else if (child.item == "HR_CENTER_HR_NOTICES") {
            child.isDisabled = !isHrNoticesEnabled(onboardingProgress.hrCenterOnboardingProgress);
          }
        }
      }

      if (item === "DATA_MAP_DASHBOARD") {
        let showBusinessSurveyIncompleteBadge = !isBusinessSurveyFinished || !isHrSurveyFinished;
        let showVendorDocumentationIncompleteBadge = isVendorDocumentationIncomplete;
        let showDataRetentionIncompleteBadge = !isDataRetentionComplete;

        showIncompleteBadge =
          showBusinessSurveyIncompleteBadge ||
          showVendorDocumentationIncompleteBadge ||
          showDataRetentionIncompleteBadge;

        for (let child of navItemChildrenMutable) {
          if (child.item == "BUSINESS_SURVEY") {
            child.showIncompleteBadge = showBusinessSurveyIncompleteBadge;
          } else if (child.item == "VENDOR_DOCUMENTATION") {
            child.showIncompleteBadge = showVendorDocumentationIncompleteBadge;
          } else if (child.item == "DATA_RETENTION") {
            child.showIncompleteBadge = showDataRetentionIncompleteBadge;
          }
        }
      }

      if (item === "WORKFLOWS") {
        // If the API integrations setup is already complete, then always enable the top-level workflows nav item
        isDisabled =
          !isPrivacyCenterOnboardingComplete(onboardingProgress.privacyCenterOnboardingProgress) &&
          !isFinishedWithApiIntegrationsSetup;

        showIncompleteBadge =
          !isWorkflowsOnboardingComplete(onboardingProgress.workflowsSetupOnboardingProgress) ||
          (isWorkflowsOnboardingComplete(onboardingProgress.workflowsSetupOnboardingProgress) &&
            !isLimitSettingsComplete);

        for (let child of navItemChildrenMutable) {
          if (child.item == "WORKFLOWS_API_INTEGRATIONS") {
            // This step is always enabled since it's the first step
            child.isDisabled = false;
          } else if (child.item == "WORKFLOWS_DATA_STORAGE_SETTINGS") {
            child.isDisabled = !(
              isFinishedWithApiIntegrationsSetup || isFinishedDataStorageOnboarding
            );
          } else if (child.item == "WORKFLOWS_REQUESTS_WORKFLOWS") {
            child.isDisabled = !(
              isFinishedDataStorageOnboarding || isFinishedWorkflowSetupOnboarding
            );
            // Only show the incomplete badge if we're not in the guided first-time onboarding
            child.showIncompleteBadge =
              isFinishedWorkflowSetupOnboarding && !isLimitSettingsComplete;
            child.route = child.showIncompleteBadge
              ? Routes.organization.requestHandling.requestToLimit
              : Routes.organization.requestHandling.base;
          } else if (child.item == "WORKFLOWS_EMAIL_TEMPLATES") {
            child.isDisabled = !isFinishedWorkflowSetupOnboarding;
          }
        }
      }

      return (
        <SidebarNavLinkRedesign
          key={item}
          isDisabled={isDisabled}
          notificationCount={notificationCount}
          refresh={refreshOrg}
          navItem={{ ...navItem, showIncompleteBadge, children: navItemChildrenMutable }}
          org={org}
        />
      );
    });

  const navContent = (org?: OrganizationDto) => {
    return org && <div className="tw-boundary">{navItems(org)}</div>;
  };

  return (
    <BaseNav
      showUserNavItem
      useNewDesign
      settingsRoute={
        currentOrgUser?.role === "HR" ? null : RouteHelpers.organization.settings(organization?.id)
      }
      userProfileRoute={Routes.user.profile}
      navContent={navContent(organization)}
    />
  );
};

const navItemsForRole = (role: UserRole | undefined): (NavItem | NavDivider)[] => {
  switch (role) {
    case "ADMIN":
      return NAV_ITEMS;
    case "GENERAL":
      return NAV_ITEMS_GENERAL_ONLY;
    case "HR":
      return NAV_ITEMS_HR_ONLY;
    default:
      return [];
  }
};
